import React, { useContext, useEffect, useState } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { showToast } from '@/lib/custom-toast';
import { formatCurrencyInput, parseCurrency } from '@/utils/currencyFormat';
import { Users, DollarSign, Wallet, TrendingUp, Edit, Trash2, Shield, Key, Power, Eye, EyeOff, ArrowLeft, Crown, Search, Gem, Settings, Tag, CreditCard, AlertCircle, Zap, RotateCcw, ChevronDown, ChevronRight, MessageSquare, Clock, CheckCircle, X as XIcon } from 'lucide-react';
import { formatCurrency } from '@/utils/currencyFormat';


// Componente interno para configuración de inactividad
const InactivitySettings = ({ token }) => {
  const [timeoutMinutes, setTimeoutMinutes] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/inactivity-timeout`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeoutMinutes(response.data.timeout_minutes);
    } catch (error) {
      showToast.error('Error al cargar configuración de inactividad');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (timeoutMinutes < 1 || timeoutMinutes > 1440) {
      showToast.error('El tiempo debe estar entre 1 y 1440 minutos');
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/settings/inactivity-timeout`,
        { timeout_minutes: timeoutMinutes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(`Tiempo de inactividad actualizado a ${timeoutMinutes} minutos`);
    } catch (error) {
      showToast.error('Error al actualizar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-slate-600">Cargando...</div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">
        Configura el tiempo de inactividad después del cual los usuarios serán desconectados automáticamente.
      </p>
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
        <div className="flex-1 max-w-xs">
          <Label htmlFor="timeout">Tiempo de inactividad (minutos)</Label>
          <Input
            id="timeout"
            type="number"
            min="1"
            max="1440"
            value={timeoutMinutes}
            onChange={(e) => setTimeoutMinutes(parseInt(e.target.value) || 1)}
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">
            Rango: 1 - 1440 minutos (24 horas)
          </p>
        </div>
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? 'Guardando...' : 'Guardar Configuración'}
        </Button>
      </div>
    </div>
  );
};

// Componente para estadísticas detalladas de Voice AI
const VoiceAIStats = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/voice-packages/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching Voice AI stats:', error);
      showToast.error('Error al cargar estadísticas de Voice AI');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-slate-600">Cargando estadísticas...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border-2 border-purple-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-purple-700 font-medium">Ventas Totales</p>
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-purple-900">{stats?.total_sales || 0}</p>
          <p className="text-xs text-purple-600 mt-1">Paquetes vendidos</p>
        </div>
        
        <div className="p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border-2 border-green-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-green-700 font-medium">Ingresos Totales</p>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-green-900">
            ${(stats?.total_revenue || 0).toFixed(2)}
          </p>
          <p className="text-xs text-green-600 mt-1">MXN generados</p>
        </div>
        
        <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border-2 border-blue-200">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-700 font-medium">Paquete Popular</p>
            <TrendingUp className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-lg font-bold text-blue-900">
            {stats?.most_popular_package || 'N/A'}
          </p>
          <p className="text-xs text-blue-600 mt-1">Más vendido</p>
        </div>
      </div>

      {/* Sales by Month Chart */}
      {stats?.sales_by_month && stats.sales_by_month.length > 0 && (
        <div className="p-6 bg-white rounded-xl border-2 border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Ventas por Mes</h3>
          <div className="space-y-3">
            {stats.sales_by_month.map((month) => (
              <div key={month._id} className="flex items-center gap-4">
                <p className="text-sm font-medium text-slate-700 w-24">
                  {month._id}
                </p>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div 
                      className="h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded"
                      style={{ width: `${(month.count / Math.max(...stats.sales_by_month.map(m => m.count))) * 100}%` }}
                    ></div>
                    <span className="text-sm font-semibold text-slate-900">{month.count}</span>
                  </div>
                  <p className="text-xs text-slate-600">${(month.revenue || 0).toFixed(2)} MXN</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Purchases */}
      {stats?.recent_purchases && stats.recent_purchases.length > 0 && (
        <div className="p-6 bg-white rounded-xl border-2 border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">Compras Recientes</h3>
          <div className="space-y-2">
            {stats.recent_purchases.map((purchase, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">
                    {purchase.user?.username || 'Usuario desconocido'}
                  </p>
                  <p className="text-xs text-slate-600">
                    {purchase.package_name || 'Paquete'} • {new Date(purchase.created_at).toLocaleDateString('es-MX')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-green-600">
                    +{purchase.credits} créditos
                  </p>
                  <p className="text-xs text-slate-600">
                    ${purchase.amount_paid.toFixed(2)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!stats?.total_sales && (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <p className="text-slate-600">No hay ventas de Voice AI aún</p>
          <p className="text-sm text-slate-500 mt-2">Las estadísticas aparecerán cuando los usuarios compren paquetes</p>
        </div>
      )}
    </div>
  );
};


// Componente para gestión de paquetes Voice AI
const VoiceAIPackageManagement = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    credits: '',
    price_mxn: '',
    is_popular: false,
    discount_percentage: ''
  });

  useEffect(() => {
    fetchPackages();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/voice-packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPackages(response.data);
    } catch (error) {
      showToast.error('Error al cargar paquetes Voice AI');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePackage = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/voice-packages`, {
        name: formData.name,
        description: formData.description,
        credits: parseInt(formData.credits),
        price_mxn: parseFloat(formData.price_mxn),
        is_popular: formData.is_popular,
        discount_percentage: parseInt(formData.discount_percentage)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast.success('Paquete creado exitosamente');
      setShowCreateForm(false);
      setFormData({
        name: '',
        description: '',
        credits: '',
        price_mxn: '',
        is_popular: false,
        discount_percentage: ''
      });
      fetchPackages();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al crear paquete');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (packageId, currentActive) => {
    try {
      await axios.put(`${API}/admin/voice-packages/${packageId}`, {
        is_active: !currentActive
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success(`Paquete ${currentActive ? 'desactivado' : 'activado'}`);
      fetchPackages();
    } catch (error) {
      showToast.error('Error al cambiar estado del paquete');
    }
  };

  const handleInitializeDefaults = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/voice-packages/initialize-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data.message);
      fetchPackages();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al inicializar paquetes');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Setup */}
      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="font-medium text-purple-900 mb-2">⚡ Configuración Rápida</p>
            <p className="text-sm text-purple-700">
              Crea automáticamente 3 paquetes: Básico (50/$49), Popular (100/$89), Premium (250/$199)
            </p>
          </div>
          <Button
            onClick={handleInitializeDefaults}
            disabled={loading}
            size="sm"
            className="bg-purple-600 hover:bg-purple-700"
          >
            Configurar
          </Button>
        </div>
      </div>

      {/* Stats Row */}
      {packages.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-xs text-purple-700 font-medium mb-1">Total Paquetes</p>
            <p className="text-2xl font-bold text-purple-900">{packages.length}</p>
          </div>
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-xs text-green-700 font-medium mb-1">Ventas Totales</p>
            <p className="text-2xl font-bold text-green-900">
              {packages.reduce((sum, p) => sum + (p.total_purchases || 0), 0)}
            </p>
          </div>
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700 font-medium mb-1">Ingresos Totales</p>
            <p className="text-2xl font-bold text-blue-900">
              ${packages.reduce((sum, p) => sum + (p.total_revenue || 0), 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Create Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-slate-900">Paquetes Existentes</h3>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          size="sm"
          className="bg-green-600 hover:bg-green-700"
        >
          {showCreateForm ? 'Cancelar' : '+ Crear Paquete'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="p-6 bg-slate-50 border-2 border-slate-200">
          <h4 className="font-bold text-slate-900 mb-4">Crear Nuevo Paquete</h4>
          <form onSubmit={handleCreatePackage} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Nombre</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Paquete Básico"
                  required
                />
              </div>
              <div>
                <Label htmlFor="credits">Créditos</Label>
                <Input
                  id="credits"
                  type="text"
                  value={formData.credits ? formatCurrencyInput(formData.credits) : ''}
                  onChange={(e) => {
                    const rawValue = parseCurrency(e.target.value);
                    setFormData({ ...formData, credits: rawValue });
                  }}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="price_mxn">Precio (MXN)</Label>
                <Input
                  id="price_mxn"
                  type="text"
                  value={formData.price_mxn ? formatCurrencyInput(formData.price_mxn) : ''}
                  onChange={(e) => {
                    const rawValue = parseCurrency(e.target.value);
                    setFormData({ ...formData, price_mxn: rawValue });
                  }}
                  placeholder="$0.00"
                  required
                />
              </div>
              <div>
                <Label htmlFor="discount">Descuento (%)</Label>
                <Input
                  id="discount"
                  type="number"
                  min="0"
                  max="100"
                  value={formData.discount_percentage}
                  onChange={(e) => setFormData({ ...formData, discount_percentage: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description">Descripción</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ideal para uso ocasional"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_popular"
                checked={formData.is_popular}
                onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                className="w-4 h-4 text-purple-600 rounded"
              />
              <Label htmlFor="is_popular" className="cursor-pointer">
                ⭐ Marcar como popular (destacado)
              </Label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Creando...' : 'Crear Paquete'}
            </Button>
          </form>
        </Card>
      )}

      {/* Packages List */}
      {loading && !packages.length ? (
        <div className="text-center py-8">
          <p className="text-slate-600">Cargando paquetes...</p>
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <p className="text-slate-600">No hay paquetes Voice AI creados</p>
          <p className="text-sm text-slate-500 mt-2">Usa la configuración rápida o crea uno nuevo</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-slate-200">
                  <th className="text-left p-3 font-bold text-slate-900">Paquete</th>
                  <th className="text-left p-3 font-bold text-slate-900">Créditos</th>
                  <th className="text-left p-3 font-bold text-slate-900">Precio</th>
                  <th className="text-left p-3 font-bold text-slate-900">$/Crédito</th>
                  <th className="text-left p-3 font-bold text-slate-900">Ventas</th>
                  <th className="text-left p-3 font-bold text-slate-900">Ingresos</th>
                  <th className="text-left p-3 font-bold text-slate-900">Estado</th>
                  <th className="text-left p-3 font-bold text-slate-900">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((pkg) => (
                  <tr key={pkg.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-3">
                      <div>
                        <span className="font-semibold text-slate-900">{pkg.name}</span>
                        {pkg.is_popular && (
                          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                            ⭐ Popular
                          </span>
                        )}
                        {pkg.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{pkg.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <span className="font-bold text-purple-600">{pkg.credits}</span>
                    </td>
                    <td className="p-3">
                      <span className="font-semibold text-slate-900">${pkg.price_mxn} MXN</span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">
                        ${(pkg.price_mxn / pkg.credits).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-700">{pkg.total_purchases || 0}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-green-700 font-semibold">
                        ${(pkg.total_revenue || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        pkg.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {pkg.is_active ? '✓ Activo' : '✗ Inactivo'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleToggleActive(pkg.id, pkg.is_active)}
                          size="sm"
                          variant="outline"
                          className={pkg.is_active ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'}
                        >
                          {pkg.is_active ? 'Desactivar' : 'Activar'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-3">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="p-4 bg-white border-2 border-slate-200">
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <span className="font-bold text-lg text-slate-900">{pkg.name}</span>
                      {pkg.is_popular && (
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded-full">
                          ⭐ Popular
                        </span>
                      )}
                      {pkg.description && (
                        <p className="text-xs text-slate-500 mt-1">{pkg.description}</p>
                      )}
                    </div>
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                      pkg.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {pkg.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 py-2 border-y border-slate-200">
                    <div>
                      <p className="text-xs text-slate-500">Créditos</p>
                      <p className="text-lg font-bold text-purple-600">{pkg.credits}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Precio</p>
                      <p className="text-lg font-bold text-slate-900">${pkg.price_mxn}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Ventas</p>
                      <p className="text-sm font-semibold text-slate-700">{pkg.total_purchases || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Ingresos</p>
                      <p className="text-sm font-semibold text-green-700">${(pkg.total_revenue || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  <Button
                    onClick={() => handleToggleActive(pkg.id, pkg.is_active)}
                    size="sm"
                    className={`w-full ${pkg.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}
                  >
                    {pkg.is_active ? 'Desactivar Paquete' : 'Activar Paquete'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


// Componente para gestión de Feedback/Reportes de usuarios
const FeedbackManagement = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [feedbacks, setFeedbacks] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');

  const feedbackTypes = {
    'complaint': { label: 'Queja', emoji: '⚠️', color: 'red' },
    'suggestion': { label: 'Sugerencia', emoji: '💡', color: 'yellow' },
    'bug': { label: 'Bug', emoji: '🐛', color: 'purple' },
    'other': { label: 'Otro', emoji: '📝', color: 'gray' }
  };

  const statusConfig = {
    'pending': { label: 'Pendiente', icon: Clock, color: 'yellow' },
    'in_review': { label: 'En Revisión', icon: Eye, color: 'blue' },
    'resolved': { label: 'Resuelta', icon: CheckCircle, color: 'green' },
    'closed': { label: 'Cerrada', icon: XIcon, color: 'gray' }
  };

  useEffect(() => {
    fetchFeedbacks();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter]);

  const fetchFeedbacks = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.append('type_filter', typeFilter);
      
      const response = await axios.get(`${API}/admin/feedback?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedbacks(response.data.feedback || []);
    } catch (error) {
      showToast.error('Error al cargar reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenResponse = (feedback) => {
    setSelectedFeedback(feedback);
    setAdminResponse(feedback.admin_response || '');
    setNewStatus(feedback.status);
    setShowResponseModal(true);
  };

  const handleUpdateFeedback = async () => {
    if (!selectedFeedback || !newStatus) return;

    try {
      await axios.put(
        `${API}/admin/feedback/${selectedFeedback.id}`,
        { status: newStatus, admin_response: adminResponse || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('Reporte actualizado correctamente');
      setShowResponseModal(false);
      setSelectedFeedback(null);
      setAdminResponse('');
      fetchFeedbacks();
    } catch (error) {
      showToast.error('Error al actualizar reporte');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const getStatsCount = (status) => {
    return feedbacks.filter(f => f.status === status).length;
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-yellow-600" />
            <p className="text-sm text-yellow-700 font-medium">Pendientes</p>
          </div>
          <p className="text-2xl font-bold text-yellow-900">
            {feedbacks.filter(f => f.status === 'pending').length}
          </p>
        </Card>
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-blue-600" />
            <p className="text-sm text-blue-700 font-medium">En Revisión</p>
          </div>
          <p className="text-2xl font-bold text-blue-900">
            {feedbacks.filter(f => f.status === 'in_review').length}
          </p>
        </Card>
        <Card className="p-4 bg-green-50 border-green-200">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <p className="text-sm text-green-700 font-medium">Resueltas</p>
          </div>
          <p className="text-2xl font-bold text-green-900">
            {feedbacks.filter(f => f.status === 'resolved').length}
          </p>
        </Card>
        <Card className="p-4 bg-slate-50 border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <XIcon className="w-4 h-4 text-slate-600" />
            <p className="text-sm text-slate-700 font-medium">Cerradas</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {feedbacks.filter(f => f.status === 'closed').length}
          </p>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="complaint">⚠️ Quejas</SelectItem>
            <SelectItem value="suggestion">💡 Sugerencias</SelectItem>
            <SelectItem value="bug">🐛 Bugs</SelectItem>
            <SelectItem value="other">📝 Otros</SelectItem>
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="pending">Pendientes</SelectItem>
            <SelectItem value="in_review">En Revisión</SelectItem>
            <SelectItem value="resolved">Resueltas</SelectItem>
            <SelectItem value="closed">Cerradas</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Feedbacks List */}
      {loading ? (
        <div className="text-center py-8">
          <p className="text-slate-600">Cargando reportes...</p>
        </div>
      ) : feedbacks.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <MessageSquare className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">No hay reportes</p>
        </div>
      ) : (
        <div className="space-y-4">
          {feedbacks.map((feedback) => {
            const type = feedbackTypes[feedback.type];
            const status = statusConfig[feedback.status];
            const StatusIcon = status.icon;
            
            return (
              <Card key={feedback.id} className="p-4 sm:p-5 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className={`p-2 rounded-lg bg-${type.color}-100 flex-shrink-0`}>
                      {type.emoji}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-lg text-slate-900 mb-1">
                        {feedback.subject}
                      </h3>
                      <p className="text-sm text-slate-600 mb-2">
                        Por: <span className="font-medium">{feedback.user_name || feedback.user_email}</span>
                      </p>
                      <p className="text-xs text-slate-500">
                        {new Date(feedback.created_at).toLocaleDateString('es-MX', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap bg-${status.color}-100 text-${status.color}-700`}>
                    <StatusIcon className="w-4 h-4" />
                    {status.label}
                  </span>
                </div>

                <p className="text-sm text-slate-700 mb-3 bg-slate-50 p-3 rounded-lg">
                  {feedback.description}
                </p>

                {feedback.screenshot_url && (
                  <div className="mb-3">
                    <Button
                      onClick={() => {
                        setSelectedImage(feedback.screenshot_url);
                        setShowImageModal(true);
                      }}
                      variant="outline"
                      size="sm"
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      📷 Ver captura de pantalla
                    </Button>
                  </div>
                )}

                {feedback.admin_response && (
                  <div className="mb-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Respuesta del equipo:
                    </p>
                    <p className="text-sm text-blue-800">
                      {feedback.admin_response}
                    </p>
                  </div>
                )}

                <Button
                  onClick={() => handleOpenResponse(feedback)}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {feedback.admin_response ? 'Editar Respuesta' : 'Responder'}
                </Button>
              </Card>
            );
          })}
        </div>
      )}

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Responder a Reporte</DialogTitle>
            <DialogDescription>Gestiona el estado y la respuesta para el reporte del usuario.</DialogDescription>
          </DialogHeader>
          
          {selectedFeedback && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600 mb-1">Asunto:</p>
                <p className="font-semibold text-slate-900">{selectedFeedback.subject}</p>
              </div>

              <div>
                <Label>Cambiar Estado</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="in_review">En Revisión</SelectItem>
                    <SelectItem value="resolved">Resuelta</SelectItem>
                    <SelectItem value="closed">Cerrada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Respuesta al Usuario (Opcional)</Label>
                <textarea
                  value={adminResponse}
                  onChange={(e) => setAdminResponse(e.target.value)}
                  placeholder="Escribe tu respuesta aquí..."
                  className="w-full h-32 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleUpdateFeedback}
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                >
                  Guardar Cambios
                </Button>
                <Button
                  onClick={() => setShowResponseModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Modal */}
      <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Captura de Pantalla</DialogTitle>
            <DialogDescription>Vista ampliada de la imagen adjunta al reporte.</DialogDescription>
          </DialogHeader>
          <div className="relative w-full h-full flex items-center justify-center bg-slate-50 rounded-lg overflow-auto">
            <img 
              src={selectedImage} 
              alt="Screenshot" 
              className="max-w-full max-h-[70vh] object-contain"
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={() => setShowImageModal(false)}
              className="bg-slate-600 hover:bg-slate-700"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// Componente para gestión completa de códigos promocionales
const PromoCodeManagement = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [promoCodes, setPromoCodes] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    discountType: 'percent',
    percent_off: 10,
    amount_off: 100,
    currency: 'mxn',
    duration: 'once',
    max_redemptions: '',
    expires_days: ''
  });

  useEffect(() => {
    fetchPromoCodes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPromoCodes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/promotion-codes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromoCodes(response.data.promotion_codes);
    } catch (error) {
      showToast.error('Error al cargar códigos promocionales');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        code: formData.code.toUpperCase(),
        duration: formData.duration
      };

      if (formData.duration === 'repeating' && formData.duration_in_months) {
        payload.duration_in_months = parseInt(formData.duration_in_months);
      }

      if (formData.discountType === 'percent') {
        payload.percent_off = parseInt(formData.percent_off);
      } else {
        payload.amount_off = parseInt(formData.amount_off);
        payload.currency = formData.currency;
      }

      if (formData.max_redemptions) {
        payload.max_redemptions = parseInt(formData.max_redemptions);
      }

      if (formData.expires_days) {
        payload.expires_days = parseInt(formData.expires_days);
      }

      await axios.post(`${API}/admin/promotion-codes`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      showToast.success('Código creado exitosamente');
      setShowCreateForm(false);
      setFormData({
        code: '',
        discountType: 'percent',
        percent_off: 10,
        amount_off: 100,
        currency: 'mxn',
        duration: 'once',
        duration_in_months: 1,
        max_redemptions: '',
        expires_days: ''
      });
      fetchPromoCodes();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al crear código');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToOnce = async (promoId, code) => {
    if (!window.confirm(`¿Convertir el código "${code}" a 1 solo cobro (once)? En Stripe se creará una versión de 1 solo uso y se desactivará la recurrente.`)) {
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/promotion-codes/${promoId}/convert-to-once`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success(res.data.message || 'Código actualizado a 1 solo cobro');
      fetchPromoCodes();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al convertir código');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (promoId, currentActive) => {
    try {
      await axios.patch(`${API}/admin/promotion-codes/${promoId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success(`Código ${currentActive ? 'desactivado' : 'activado'}`);
      fetchPromoCodes();
    } catch (error) {
      showToast.error('Error al cambiar estado del código');
    }
  };

  const handleQuickSetup = async () => {
    setLoading(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await axios.post(
        `${API}/admin/setup-promotion-codes`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('Códigos predeterminados configurados');
      fetchPromoCodes();
    } catch (error) {
      showToast.error('Error al configurar códigos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Quick Setup */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="font-medium text-blue-900 mb-2">⚡ Configuración Rápida</p>
            <p className="text-sm text-blue-700">
              Crea automáticamente los 5 códigos predeterminados (LANZAMIENTO5, PREMIUM10, ANUAL15, AMIGO20, FAMILIA50)
            </p>
          </div>
          <Button
            onClick={handleQuickSetup}
            disabled={loading}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            Configurar
          </Button>
        </div>
      </div>

      {/* Create New Code Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-bold text-slate-900">Códigos Existentes</h3>
        <Button
          onClick={() => setShowCreateForm(!showCreateForm)}
          size="sm"
          className="bg-green-600 hover:bg-green-700"
        >
          {showCreateForm ? 'Cancelar' : '+ Crear Código'}
        </Button>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="p-6 bg-slate-50 border-2 border-slate-200">
          <h4 className="font-bold text-slate-900 mb-4">Crear Nuevo Código</h4>
          <form onSubmit={handleCreateCode} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code">Código</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="VERANO2025"
                  required
                  className="uppercase"
                />
              </div>

              <div>
                <Label htmlFor="discountType">Tipo de Descuento</Label>
                <Select
                  value={formData.discountType}
                  onValueChange={(value) => setFormData({ ...formData, discountType: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentaje (%)</SelectItem>
                    <SelectItem value="amount">Cantidad Fija</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              {formData.discountType === 'percent' ? (
                <div>
                  <Label htmlFor="percent_off">Porcentaje de Descuento</Label>
                  <Input
                    id="percent_off"
                    type="number"
                    min="1"
                    max="100"
                    value={formData.percent_off}
                    onChange={(e) => setFormData({ ...formData, percent_off: e.target.value })}
                    required
                  />
                </div>
              ) : (
                <>
                  <div>
                    <Label htmlFor="amount_off">Cantidad de Descuento</Label>
                    <Input
                      id="amount_off"
                      type="text"
                      value={formData.amount_off ? formatCurrencyInput(formData.amount_off) : ''}
                      onChange={(e) => {
                        const rawValue = parseCurrency(e.target.value);
                        setFormData({ ...formData, amount_off: rawValue });
                      }}
                      placeholder="$0.00"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="currency">Moneda</Label>
                    <Select
                      value={formData.currency}
                      onValueChange={(value) => setFormData({ ...formData, currency: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mxn">MXN</SelectItem>
                        <SelectItem value="usd">USD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            {/* Duración del Descuento */}
            <div className="grid sm:grid-cols-2 gap-4 bg-white p-3 rounded-lg border border-slate-200">
              <div>
                <Label htmlFor="duration" className="font-bold text-slate-800">Duración del Descuento</Label>
                <Select
                  value={formData.duration}
                  onValueChange={(value) => setFormData({ ...formData, duration: value })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="once">⚡ 1 solo cobro (Predeterminado / Recomendado)</SelectItem>
                    <SelectItem value="repeating">🔄 Varios meses</SelectItem>
                    <SelectItem value="forever">⚠️ Para siempre (Permanente)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1">
                  {formData.duration === 'once' && 'Aplica únicamente al primer cobro / factura inicial. Las renovaciones cobrarán precio regular.'}
                  {formData.duration === 'repeating' && 'Aplica durante los meses especificados y luego vuelve al precio regular.'}
                  {formData.duration === 'forever' && 'CUIDADO: Se aplicará en todas las renovaciones y upgrades indefinidamente.'}
                </p>
              </div>

              {formData.duration === 'repeating' ? (
                <div>
                  <Label htmlFor="duration_in_months" className="font-bold text-slate-800">Número de Meses</Label>
                  <Input
                    id="duration_in_months"
                    type="number"
                    min="1"
                    max="36"
                    value={formData.duration_in_months || 1}
                    onChange={(e) => setFormData({ ...formData, duration_in_months: parseInt(e.target.value) || 1 })}
                    required
                    className="mt-1"
                  />
                </div>
              ) : (
                <div className="hidden sm:block" />
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="max_redemptions">Usos Máximos (Límite de usuarios)</Label>
                <Input
                  id="max_redemptions"
                  type="number"
                  min="1"
                  value={formData.max_redemptions}
                  onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value })}
                  placeholder="Ej. 10 (Vacío = Ilimitado)"
                />
                <p className="text-[11px] text-slate-500 mt-1">Total de usuarios que pueden canjear este código.</p>
              </div>

              <div>
                <Label htmlFor="expires_days">Expira en (días, Opcional)</Label>
                <Input
                  id="expires_days"
                  type="number"
                  min="1"
                  value={formData.expires_days}
                  onChange={(e) => setFormData({ ...formData, expires_days: e.target.value })}
                  placeholder="Sin expiración"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Creando...' : 'Crear Código Promocional'}
            </Button>
          </form>
        </Card>
      )}

      {/* Codes List */}
      {loading && !promoCodes.length ? (
        <div className="text-center py-8">
          <p className="text-slate-600">Cargando códigos...</p>
        </div>
      ) : promoCodes.length === 0 ? (
        <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <p className="text-slate-600">No hay códigos promocionales creados</p>
          <p className="text-sm text-slate-500 mt-2">Usa la configuración rápida o crea uno nuevo</p>
        </div>
      ) : (
        <>
          {/* Vista de tabla para desktop */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100 border-b-2 border-slate-200">
                  <th className="text-left p-3 font-bold text-slate-900">Código</th>
                  <th className="text-left p-3 font-bold text-slate-900">Descuento</th>
                  <th className="text-left p-3 font-bold text-slate-900">Duración</th>
                  <th className="text-left p-3 font-bold text-slate-900">Usos</th>
                  <th className="text-left p-3 font-bold text-slate-900">Expira</th>
                  <th className="text-left p-3 font-bold text-slate-900">Estado</th>
                  <th className="text-left p-3 font-bold text-slate-900">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {promoCodes.map((promo) => (
                  <tr key={promo.id} className="border-b border-slate-200 hover:bg-slate-50">
                    <td className="p-3">
                      <span className="font-mono font-bold text-slate-900">{promo.code}</span>
                    </td>
                    <td className="p-3">
                      <span className="text-slate-700 font-semibold">
                        {promo.percent_off ? `${promo.percent_off}%` : `$${promo.amount_off} ${promo.currency?.toUpperCase()}`}
                      </span>
                    </td>
                    <td className="p-3">
                      {promo.duration === 'once' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                          ⚡ 1 solo cobro
                        </span>
                      ) : promo.duration === 'repeating' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
                          🔄 {promo.duration_in_months || '?'} meses
                        </span>
                      ) : (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                            ⚠️ Para siempre
                          </span>
                          <button
                            type="button"
                            onClick={() => handleConvertToOnce(promo.id, promo.code)}
                            title="Convertir a 1 solo cobro (once) en Stripe"
                            className="text-[10px] px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold rounded border border-indigo-200 transition-colors"
                          >
                            Pasar a 1 cobro
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-slate-700 font-medium">
                        {promo.times_redeemed} {promo.max_redemptions ? `/ ${promo.max_redemptions}` : '/ ∞'}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-slate-600">
                        {promo.expires_at 
                          ? new Date(promo.expires_at * 1000).toLocaleDateString('es-MX')
                          : 'Nunca'
                        }
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        promo.active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {promo.active ? '✓ Activo' : '✗ Inactivo'}
                      </span>
                    </td>
                    <td className="p-3">
                      <Button
                        onClick={() => handleToggleActive(promo.id, promo.active)}
                        size="sm"
                        variant="outline"
                        className={promo.active ? 'border-red-300 text-red-600 hover:bg-red-50' : 'border-green-300 text-green-600 hover:bg-green-50'}
                      >
                        {promo.active ? 'Desactivar' : 'Activar'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Vista de cards para móvil */}
          <div className="lg:hidden space-y-3">
            {promoCodes.map((promo) => (
              <Card key={promo.id} className="p-4 bg-white border-2 border-slate-200">
                <div className="space-y-3">
                  {/* Header con código y estado */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="font-mono font-bold text-lg text-slate-900 block">{promo.code}</span>
                      <span className="text-xl font-bold text-blue-600 block mt-1">
                        {promo.percent_off ? `${promo.percent_off}% OFF` : `$${promo.amount_off} ${promo.currency?.toUpperCase()} OFF`}
                      </span>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold flex-shrink-0 ${
                      promo.active 
                        ? 'bg-green-100 text-green-800 border-2 border-green-300' 
                        : 'bg-red-100 text-red-800 border-2 border-red-300'
                    }`}>
                      {promo.active ? '✓ Activo' : '✗ Inactivo'}
                    </span>
                  </div>

                  {/* Detalles */}
                  <div className="grid grid-cols-2 gap-3 py-3 border-y border-slate-200">
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Duración</p>
                      <div className="mt-1 flex items-center gap-1 flex-wrap">
                        {promo.duration === 'once' ? (
                          <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">⚡ 1 cobro</span>
                        ) : promo.duration === 'repeating' ? (
                          <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">🔄 {promo.duration_in_months || '?'} m</span>
                        ) : (
                          <div className="space-y-1">
                            <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded block">⚠️ Siempre</span>
                            <button
                              type="button"
                              onClick={() => handleConvertToOnce(promo.id, promo.code)}
                              className="text-[10px] px-1.5 py-0.5 bg-indigo-600 text-white font-bold rounded active:scale-95 block"
                            >
                              A 1 cobro
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Usos</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">
                        {promo.times_redeemed} {promo.max_redemptions ? `/ ${promo.max_redemptions}` : '/ ∞'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 font-medium">Expira</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">
                        {promo.expires_at 
                          ? new Date(promo.expires_at * 1000).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })
                          : 'Nunca'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Acción */}
                  <Button
                    onClick={() => handleToggleActive(promo.id, promo.active)}
                    size="sm"
                    className={`w-full ${promo.active ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                  >
                    {promo.active ? 'Desactivar Código' : 'Activar Código'}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
};


const UserMultiSelect = ({ token, value, onChange }) => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const selectedEmails = value ? value.split(',').map(e => e.trim()).filter(e => e) : [];

  useEffect(() => {
    axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUsers(res.data))
      .catch(err => console.error('Error fetching users for select:', err));
  }, [token]);

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(search.toLowerCase()) || 
    (u.name && u.name.toLowerCase().includes(search.toLowerCase()))
  );

  const toggleEmail = (email) => {
    const newSelected = selectedEmails.includes(email)
      ? selectedEmails.filter(e => e !== email)
      : [...selectedEmails, email];
    onChange(newSelected.join(', '));
    setSearch('');
  };

  const removeEmail = (email) => {
    onChange(selectedEmails.filter(e => e !== email).join(', '));
  };

  return (
    <div className="relative w-full">
      <div className="flex flex-wrap gap-2 mb-2">
        {selectedEmails.map(email => (
          <span key={email} className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center gap-1 border border-purple-200">
            {email}
            <button type="button" onClick={() => removeEmail(email)} className="hover:bg-purple-200 rounded-full p-0.5">
              <XIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="relative">
        <Input
          type="text"
          placeholder="Buscar por email o nombre..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => setIsOpen(true)}
          className="w-full bg-white"
        />
        {isOpen && search && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
            {filteredUsers.length > 0 ? (
              filteredUsers.map(user => (
                <div 
                  key={user.id} 
                  onClick={() => toggleEmail(user.email)}
                  className="px-4 py-2 hover:bg-slate-50 cursor-pointer flex justify-between items-center border-b last:border-b-0"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-900">{user.name || 'Sin nombre'}</p>
                    <p className="text-xs text-slate-500">{user.email}</p>
                  </div>
                  {selectedEmails.includes(user.email) && <CheckCircle className="w-4 h-4 text-green-500" />}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-slate-500 text-center">No se encontraron usuarios</div>
            )}
          </div>
        )}
      </div>
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>}
    </div>
  );
};

// Componente para el Centro de Mensajes / Notificaciones Push
const AdminNotifications = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    image_url: '',
    action_url: '',
    target: 'all',
    target_user_email: ''
  });

  useEffect(() => {
    fetchHistory();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/admin/notifications/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleBroadcastUpdate = async () => {
    if (!window.confirm('¿Estás seguro de enviar el aviso de actualización a TODOS los usuarios (Android y PWA)?')) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/broadcast-update-alert`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success(response.data.message);
      fetchHistory();
    } catch (error) {
      showToast.error('Error al difundir actualización: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.body) {
      showToast.error('Título y mensaje son obligatorios');
      return;
    }

    if (!window.confirm(`¿Estás seguro de enviar esta notificación a: ${formData.target}?`)) return;

    setLoading(true);
    try {
      await axios.post(`${API}/admin/broadcast-notification`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success('Notificación enviada correctamente');
      setFormData({ ...formData, title: '', body: '', image_url: '', action_url: '' });
      fetchHistory();
    } catch (error) {
      showToast.error('Error al enviar notificación: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReuse = (item) => {
    setFormData({
      ...formData,
      title: item.title,
      body: item.body,
      image_url: item.image_url || '',
      action_url: item.action_url || '',
      target: item.target_audience,
      target_user_email: item.target_user_email || ''
    });
    // Scroll al formulario de notificaciones de forma ms suave
    document.getElementById('notification-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    showToast.success('Datos cargados en el formulario');
  };

  const handleDeleteNotification = async (notifId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta notificación? Se borrará del historial y de la campanita de todos los usuarios.')) return;

    try {
      await axios.delete(`${API}/admin/notifications/${notifId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success('Notificación eliminada correctamente');
      fetchHistory();
    } catch (error) {
      showToast.error('Error al eliminar: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulario */}
        <Card id="notification-form" className="p-6 bg-slate-50 border-2 border-slate-200 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Zap className="w-5 h-5 text-purple-600" />
              Redactar Nueva Notificación
            </h3>
            <Button 
              onClick={handleBroadcastUpdate}
              disabled={loading}
              variant="outline"
              className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100 hover:text-blue-800 border-2"
              size="sm"
            >
              🚀 Avisar Nueva Versión
            </Button>
          </div>
          <form onSubmit={handleSend} className="space-y-4">
            <div className="space-y-2">
              <Label>Segmento de Audiencia</Label>
              <Select value={formData.target} onValueChange={(v) => setFormData({...formData, target: v})}>
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  <SelectItem value="free">Solo usuarios FREE</SelectItem>
                  <SelectItem value="premium">Solo usuarios PREMIUM</SelectItem>
                  <SelectItem value="pro">Solo usuarios PRO</SelectItem>
                  <SelectItem value="individual">Usuario específico (por email)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.target === 'individual' && (
              <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                <Label>Seleccionar Usuarios</Label>
                <UserMultiSelect 
                  token={token}
                  value={formData.target_user_email} 
                  onChange={(val) => setFormData({...formData, target_user_email: val})}
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Título de la Notificación</Label>
              <Input 
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="¡Nueva función disponible! 🚀"
                className="bg-white"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Cuerpo del Mensaje</Label>
              <textarea
                value={formData.body}
                onChange={(e) => setFormData({...formData, body: e.target.value})}
                placeholder="Descubre cómo mejorar tus ahorros con nuestra nueva herramienta..."
                className="w-full h-24 p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 bg-white text-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>URL de Imagen (Opcional - Formato 2:1 recomendado)</Label>
              <Input 
                value={formData.image_url} 
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://tuservidor.com/imagen_promo.jpg"
                className="bg-white"
              />
              <p className="text-[10px] text-slate-500 italic">Las imágenes hacen que tus mensajes destaquen más en el celular.</p>
            </div>

            <div className="space-y-2">
              <Label>Ruta de la App (Opcional - Deep Linking)</Label>
              <Input 
                value={formData.action_url} 
                onChange={(e) => setFormData({...formData, action_url: e.target.value})}
                placeholder="Ej: /pricing o /goals"
                className="bg-white"
              />
              <p className="text-[10px] text-slate-500 italic">El usuario irá a esta sección al hacer clic.</p>
            </div>

            <Button 
              type="submit" 
              disabled={loading} 
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12 mt-4 shadow-md"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4 animate-spin" /> Enviando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Zap className="w-4 h-4" /> Enviar Notificación Push
                </span>
              )}
            </Button>
          </form>
        </Card>

        {/* Historial de Envíos */}
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-500" />
              Historial de Mensajes
            </h3>
            <Button variant="ghost" size="sm" onClick={fetchHistory} className="text-blue-600 text-xs">
              Actualizar
            </Button>
          </div>
          
          <div className="space-y-3 max-h-[650px] overflow-y-auto pr-2 custom-scrollbar">
            {history.map((item) => (
              <Card key={item.id} className="p-4 bg-white border border-slate-200 hover:border-purple-300 transition-colors">
                <div className="flex justify-between items-start mb-2">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                    item.target_audience === 'all' ? 'bg-blue-100 text-blue-700' :
                    item.target_audience === 'pro' ? 'bg-purple-100 text-purple-700' :
                    item.target_audience === 'premium' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    Para: {item.target_audience}
                  </span>
                  <span className="text-[10px] text-slate-400 font-medium">
                    {new Date(item.sent_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
                <div className="flex justify-between items-start gap-4">
                  <div className="flex-1">
                    <p className="font-bold text-slate-900 text-sm">{item.title}</p>
                    <p className="text-xs text-slate-600 line-clamp-2 mt-1">{item.body}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleReuse(item)}
                      className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                      title="Reutilizar / Editar"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDeleteNotification(item.id)}
                      className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                      title="Eliminar de raíz"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {item.image_url && (
                  <div className="mt-2 text-[10px] flex items-center gap-1 text-purple-600 font-medium">
                    🖼️ Incluye imagen
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-4">
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    <span className="text-[11px] font-bold text-green-700">{item.success_count}</span>
                    <span className="text-[10px] text-slate-500">entregados</span>
                  </div>
                  {item.failure_count > 0 && (
                    <div className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 text-red-400" />
                      <span className="text-[11px] font-bold text-red-600">{item.failure_count}</span>
                      <span className="text-[10px] text-slate-500">fallidos</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
            
            {history.length === 0 && (
              <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No hay campañas enviadas aún</p>
                <p className="text-[11px] text-slate-400 mt-1">Tus notificaciones enviadas aparecerán aquí.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const AdminPanel = () => {
  const { token, refreshUser } = useContext(AuthContext);
  const [activeAdminTab, setActiveAdminTab] = useState('usuarios');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('main'); // 'main' or 'user-stats'
  const [editingUser, setEditingUser] = useState(null);
  const [changingPassword, setChangingPassword] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [searchQuery, setSearchQuery] = useState(''); // Estado para el buscador
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState(null);
  const [isSubscriptionOptionsModalOpen, setIsSubscriptionOptionsModalOpen] = useState(false);
  const [selectedUserForOptions, setSelectedUserForOptions] = useState(null);
  const [stripeActionLoading, setStripeActionLoading] = useState(false);
  const [isVoiceCreditsDialogOpen, setIsVoiceCreditsDialogOpen] = useState(false);
  const [selectedUserForVoiceCredits, setSelectedUserForVoiceCredits] = useState(null);
  const [voiceCreditsAmount, setVoiceCreditsAmount] = useState('');
  const [voiceMonthlyUsageAmount, setVoiceMonthlyUsageAmount] = useState('');
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    tier: 'premium',
    duration: 'monthly',
    trial_days: 0
  });
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: 'user'
  });
  const [passwordData, setPasswordData] = useState({
    new_password: ''
  });

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      if (refreshUser) refreshUser();
    } catch (error) {
      showToast.error('Error al cargar datos de administración');
    } finally {
      setLoading(false);
    }

  };

  const fetchUserStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/user-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(res.data);
      setView('user-stats');
    } catch (error) {
      showToast.error('Error al cargar estadísticas de usuarios');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success('Usuario actualizado');
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al actualizar usuario');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password.length < 6) {
      showToast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/change-password`, {
        user_id: changingPassword.id,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success('Contraseña cambiada exitosamente');
      setIsPasswordDialogOpen(false);
      setPasswordData({ new_password: '' });
      setShowPassword(false);
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  const handleToggleStatus = async (userId) => {
    const user = users.find(u => u.id === userId);
    const action = user?.is_active ? 'desactivar' : 'activar';
    const userName = user?.username || 'este usuario';
    
    // Usar toast personalizado para confirmación
    toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Power className={`w-5 h-5 ${user?.is_active ? 'text-red-600' : 'text-green-600'}`} />
            <p className="font-semibold text-slate-900">Cambiar estado de usuario</p>
          </div>
          <p className="text-sm text-slate-600">
            ¿Estás seguro de {action} a <span className="font-semibold">{userName}</span>?
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                toast.dismiss(t);
                try {
                  const response = await axios.post(`${API}/admin/toggle-user-status/${userId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  showToast.success(response.data.message);
                  fetchData();
                } catch (error) {
                  showToast.error(error.response?.data?.detail || 'Error al cambiar estado');
                }
              }}
              className={`flex-1 px-3 py-2 ${user?.is_active ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white text-sm font-medium rounded-lg transition-colors`}
            >
              Sí, {action}
            </button>
            <button
              onClick={() => toast.dismiss(t)}
              className="flex-1 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
        style: {
          background: 'white',
          border: `2px solid ${user?.is_active ? '#ef4444' : '#10b981'}`,
          borderRadius: '16px',
          padding: '20px',
          boxShadow: `0 20px 50px ${user?.is_active ? 'rgba(239, 68, 68, 0.3)' : 'rgba(16, 185, 129, 0.3)'}`,
          maxWidth: '400px',
          width: '90vw',
        },
      }
    );
  };

  const handleDeleteUser = async (userId) => {
    // Usar toast personalizado en lugar de window.confirm
    toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <p className="font-semibold text-slate-900">¿Eliminar usuario?</p>
          </div>
          <p className="text-sm text-slate-600">Se eliminarán todos sus datos permanentemente.</p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                toast.dismiss(t);
                try {
                  await axios.delete(`${API}/admin/users/${userId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  showToast.success('✅ Usuario eliminado correctamente');
                  fetchData();
                } catch (error) {
                  showToast.error('❌ ' + (error.response?.data?.detail || 'Error al eliminar usuario'));
                }
              }}
              className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Sí, eliminar
            </button>
            <button
              onClick={() => toast.dismiss(t)}
              className="flex-1 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'bottom-center',
        style: {
          background: 'white',
          border: '2px solid #ef4444',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 20px 50px rgba(239, 68, 68, 0.3)',
          maxWidth: '400px',
          width: '90vw',
        },
      }
    );
  };

  // eslint-disable-next-line no-unused-vars
  const handleRevokeSubscription = async (userId, userName) => {
    toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="font-semibold text-slate-900">Revocar Suscripción</p>
          </div>
          <p className="text-sm text-slate-600">
            ¿Revocar suscripción de <span className="font-semibold">{userName}</span> y cambiar a Free?
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                toast.dismiss(t);
                try {
                  await axios.post(`${API}/admin/revoke-subscription/${userId}`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  showToast.success('Suscripción revocada exitosamente');
                  fetchData();
                } catch (error) {
                  showToast.error(error.response?.data?.detail || 'Error al revocar suscripción');
                }
              }}
              className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg"
            >
              Sí, revocar
            </button>
            <button
              onClick={() => toast.dismiss(t)}
              className="flex-1 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-medium rounded-lg"
            >
              Cancelar
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
        style: {
          background: 'white',
          border: '2px solid #ef4444',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 20px 50px rgba(239, 68, 68, 0.3)',
          maxWidth: '400px',
          width: '90vw',
        },
      }
    );
  };

  const handleRefundAndCancel = async (userId, userName) => {
    if (!window.confirm(`¿Seguro que deseas REEMBOLSAR el último cobro y CANCELAR de inmediato la suscripción en Stripe para ${userName}? Se devolverá el dinero a su tarjeta bancaria y pasará a plan Free.`)) {
      return;
    }
    setStripeActionLoading(true);
    try {
      showToast.info('Procesando reembolso y cancelación en Stripe...');
      const response = await axios.post(
        `${API}/admin/users/${userId}/refund-and-cancel`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data?.message || 'Reembolso emitido y suscripción cancelada');
      setIsSubscriptionOptionsModalOpen(false);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al procesar reembolso y cancelación');
    } finally {
      setStripeActionLoading(false);
    }
  };

  const handleCancelStripeSubscription = async (userId, userName) => {
    if (!window.confirm(`¿Seguro que deseas CANCELAR la suscripción de Stripe para ${userName}? No se emitirá reembolso y el usuario pasará a Free.`)) {
      return;
    }
    setStripeActionLoading(true);
    try {
      showToast.info('Cancelando suscripción en Stripe...');
      const response = await axios.post(
        `${API}/admin/users/${userId}/cancel-stripe-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data?.message || 'Suscripción cancelada en Stripe');
      setIsSubscriptionOptionsModalOpen(false);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al cancelar suscripción en Stripe');
    } finally {
      setStripeActionLoading(false);
    }
  };

  const handleUpdateToCurrentPrice = async (userId, userName) => {
    if (!window.confirm(`¿Acoplar la suscripción de ${userName} al precio oficial actual de Stripe ($129.00 MXN / mes para Pro)? La próxima factura cobrará la nueva tarifa.`)) {
      return;
    }
    setStripeActionLoading(true);
    try {
      showToast.info('Actualizando precio en Stripe...');
      const response = await axios.post(
        `${API}/admin/users/${userId}/update-to-current-price`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data?.message || 'Precio de suscripción actualizado en Stripe');
      setIsSubscriptionOptionsModalOpen(false);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al actualizar precio en Stripe');
    } finally {
      setStripeActionLoading(false);
    }
  };

  const handleSyncUserStripe = async (userId, userName) => {
    setStripeActionLoading(true);
    try {
      showToast.info('Buscando suscripción activa en Stripe...');
      const response = await axios.post(
        `${API}/admin/users/${userId}/sync-stripe`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(response.data?.message || 'Usuario sincronizado con Stripe');
      setIsSubscriptionOptionsModalOpen(false);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al sincronizar con Stripe');
    } finally {
      setStripeActionLoading(false);
    }
  };

  const handleTogglePremium = async (userId, userName, isPremium) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    setSelectedUserForOptions(user);
    setIsSubscriptionOptionsModalOpen(true);
  };

  const handleGrantSubscription = async () => {
    if (!selectedUserForSubscription) return;
    
    try {
      const response = await axios.post(
        `${API}/admin/grant-subscription`,
        {
          user_id: selectedUserForSubscription.id,
          tier: subscriptionFormData.tier,
          duration: subscriptionFormData.duration,
          trial_days: parseInt(subscriptionFormData.trial_days) || 0
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showToast.success(response.data.message);
      setIsSubscriptionDialogOpen(false);
      setSelectedUserForSubscription(null);
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al otorgar suscripción');
    }
  };

  const handleSyncStripePrices = async () => {
    try {
      showToast.info('Sincronizando precios en Stripe...');
      const response = await axios.post(
        `${API}/admin/sync-stripe-prices`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success('¡Éxito! Precios de Stripe sincronizados/creados correctamente.');
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al sincronizar precios de Stripe');
    }
  };
  const handleRecoverSubscriptions = async () => {
    try {
      showToast.info('Iniciando proceso de recuperación de suscripciones...');
      const response = await axios.post(
        `${API}/admin/recover-unrenewed-subscriptions`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { recovered_count } = response.data;
      if (recovered_count > 0) {
        showToast.success(`¡Éxito! ${recovered_count} suscripción(es) recuperada(s) y cobrada(s).`);
      } else {
        showToast.info('No se encontraron suscripciones pendientes por recuperar.');
      }
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al ejecutar recuperación de suscripciones');
    }
  };

  const handleOpenVoiceCreditsDialog = (user) => {
    setSelectedUserForVoiceCredits(user);
    setVoiceCreditsAmount('');
    setVoiceMonthlyUsageAmount('');
    setIsVoiceCreditsDialogOpen(true);
  };

  const handleUpdateVoiceCredits = async () => {
    if (!selectedUserForVoiceCredits) return;
    
    const amount = parseInt(voiceCreditsAmount);
    if (isNaN(amount) || amount === 0) {
      showToast.error('Por favor ingresa un número válido');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/voice-credits/update`,
        {
          user_id: selectedUserForVoiceCredits.id,
          credits_to_add: amount
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showToast.success(response.data.message);
      setIsVoiceCreditsDialogOpen(false);
      setSelectedUserForVoiceCredits(null);
      setVoiceCreditsAmount('');
      setVoiceMonthlyUsageAmount('');
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al actualizar créditos');
    }
  };

  const handleUpdateMonthlyUsage = async () => {
    if (!selectedUserForVoiceCredits) return;
    
    const amount = parseInt(voiceMonthlyUsageAmount);
    if (isNaN(amount) || amount === 0) {
      showToast.error('Por favor ingresa un número válido');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/voice-credits/update-monthly-usage`,
        {
          user_id: selectedUserForVoiceCredits.id,
          usage_to_add: -amount  // Invertir: positivo resta uso, negativo suma uso
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      showToast.success(response.data.message);
      setIsVoiceCreditsDialogOpen(false);
      setSelectedUserForVoiceCredits(null);
      setVoiceCreditsAmount('');
      setVoiceMonthlyUsageAmount('');
      fetchData();
    } catch (error) {
      showToast.error(error.response?.data?.detail || 'Error al actualizar uso mensual');
    }
  };

  const handleResetMonthlyUsage = async (userId, username) => {
    toast(
      (t) => (
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="font-semibold text-slate-900">Resetear Uso Mensual</p>
          </div>
          <p className="text-sm text-slate-600">
            ¿Resetear contador de Voice AI de <span className="font-semibold">{username}</span>?
            <br/>
            <span className="text-xs text-amber-600">Esto eliminará el historial de este mes</span>
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={async () => {
                toast.dismiss(t);
                try {
                  const response = await axios.post(
                    `${API}/admin/voice-credits/reset-monthly?user_id=${userId}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  showToast.success(response.data.message);
                  fetchData();
                } catch (error) {
                  showToast.error('Error al resetear uso mensual');
                }
              }}
              className="flex-1 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-all"
            >
              Sí, resetear
            </button>
            <button
              onClick={() => toast.dismiss(t)}
              className="flex-1 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm font-medium rounded-lg transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        position: 'top-center',
        style: {
          background: 'white',
          border: '2px solid #f59e0b',
          borderRadius: '16px',
          padding: '20px',
          boxShadow: '0 20px 50px rgba(245, 158, 11, 0.3)',
          maxWidth: '400px',
          width: '90vw',
        },
      }
    );
  };


  // Filtrar usuarios según la búsqueda
  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      role: user.role
    });
    setIsDialogOpen(true);
  };

  const openPasswordDialog = (user) => {
    setChangingPassword(user);
    setPasswordData({ new_password: '' });
    setShowPassword(false);
    setIsPasswordDialogOpen(true);
  };


  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Cargando...</div>
        </div>
      </Layout>
    );
  }

  const adminTabs = [
    { id: 'usuarios', label: 'Usuarios', icon: Users },
    { id: 'suscripciones', label: 'Suscripciones', icon: Crown },
    { id: 'voice', label: 'Voice AI', icon: Zap },
    { id: 'mensajes', label: 'Push Pro', icon: MessageSquare },
    { id: 'reportes', label: 'Reportes', icon: AlertCircle },
    { id: 'sistema', label: 'Sistema', icon: Settings },
  ];

  return (
    <Layout>
      <div className="p-1 sm:p-6 lg:p-8 space-y-4 max-w-full overflow-x-hidden" data-testid="admin-panel-container">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-2xl flex-shrink-0">
            <Shield className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-3xl font-black text-slate-900 tracking-tight leading-tight mb-0.5">Panel de Administración</h1>
            <p className="text-xs sm:text-sm text-slate-500 font-medium truncate">Gestión completa de la plataforma</p>
          </div>
        </div>

        {/* Platform Stats Cards (Siempre visibles) */}
        {view === 'main' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
              <Card 
                className="p-3 sm:p-4 bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200 cursor-pointer hover:shadow-md transition-all relative overflow-hidden"
                onClick={() => setActiveAdminTab('usuarios')}
              >
                <div className="flex items-start justify-between gap-2 relative z-10">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs text-purple-700 font-bold uppercase tracking-wider leading-tight">Total Usuarios</p>
                    <p className="text-xl sm:text-3xl font-black text-purple-900 mt-1">{stats?.total_users || 0}</p>
                  </div>
                  <div className="p-2 sm:p-2.5 bg-purple-200/80 rounded-xl flex-shrink-0">
                    <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-700" />
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 relative overflow-hidden">
                <div className="flex items-start justify-between gap-2 relative z-10">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs text-green-700 font-bold uppercase tracking-wider leading-tight">Ingresos Plat.</p>
                    <p className="text-lg sm:text-2xl font-black text-green-900 mt-1 truncate">
                      {formatCurrency(stats?.platform_income || 0)}
                    </p>
                  </div>
                  <div className="p-2 sm:p-2.5 bg-green-200/80 rounded-xl flex-shrink-0">
                    <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-green-700" />
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4 bg-gradient-to-br from-red-50 to-rose-50 border-red-200 relative overflow-hidden">
                <div className="flex items-start justify-between gap-2 relative z-10">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs text-red-700 font-bold uppercase tracking-wider leading-tight">Gastos Plat.</p>
                    <p className="text-lg sm:text-2xl font-black text-red-900 mt-1 truncate">
                      {formatCurrency(stats?.platform_expenses || 0)}
                    </p>
                  </div>
                  <div className="p-2 sm:p-2.5 bg-red-200/80 rounded-xl flex-shrink-0">
                    <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-red-700" />
                  </div>
                </div>
              </Card>

              <Card 
                className="p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200 cursor-pointer hover:shadow-md transition-all relative overflow-hidden"
                onClick={fetchUserStats}
              >
                <div className="flex items-start justify-between gap-2 relative z-10">
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs text-blue-700 font-bold uppercase tracking-wider leading-tight">Transacciones</p>
                    <p className="text-xl sm:text-3xl font-black text-blue-900 mt-1 truncate">{stats?.total_transactions || 0}</p>
                  </div>
                  <div className="p-2 sm:p-2.5 bg-blue-200/80 rounded-xl flex-shrink-0">
                    <Wallet className="w-4 h-4 sm:w-5 sm:h-5 text-blue-700" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Admin Layout Container */}
            <div className="flex flex-col lg:flex-row gap-6 items-start">
              {/* Admin Tabs Sidebar (Desktop) / Topbar (Mobile) */}
              <div className="flex lg:flex-col items-center lg:items-stretch gap-1.5 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0 no-scrollbar border-b lg:border-transparent border-slate-200 w-full lg:w-64 flex-shrink-0 lg:pr-4">
                {adminTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const isActive = activeAdminTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveAdminTab(tab.id)}
                      className={`flex items-center gap-3 px-4 py-3 lg:py-3.5 rounded-xl font-bold text-sm whitespace-nowrap transition-all ${
                        isActive
                          ? 'bg-purple-600 text-white shadow-md shadow-purple-200'
                          : 'bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 border lg:border-transparent border-slate-200/60'
                      }`}
                    >
                      <TabIcon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>

              {/* Admin Content Area */}
              <div className="flex-1 w-full min-w-0 space-y-6">
                {/* TAB: USUARIOS */}
                {activeAdminTab === 'usuarios' && (
              <Card className="p-2.5 sm:p-6 bg-white border-slate-200 shadow-sm animate-in fade-in duration-200">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  Gestión de Usuarios ({filteredUsers.length})
                </h2>

                {/* Buscador */}
                <div className="mb-6">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <Input
                      type="text"
                      placeholder="Buscar por nombre de usuario o email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2.5 w-full border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>

                {/* Vista de tabla para desktop */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Usuario</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Email</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Rol</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Premium</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Voice AI</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Estado</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Última Conexión</th>
                        <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Verificado</th>
                        <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-slate-900">{user.username}</div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{user.email}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role === 'admin' ? 'Admin' : 'Usuario'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                ? 'bg-purple-100 text-purple-700'
                                : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                ? 'Pro'
                                : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                ? 'Premium'
                                : 'Free'
                              }
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            {(user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && 
                             (user.subscription_status === 'active' || user.subscription_status === 'trialing') ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-600">
                                    📊 Uso: {user.voice_monthly_usage || 0}/100
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleResetMonthlyUsage(user.id, user.username)}
                                    className="hover:bg-amber-100 p-1"
                                    title="Resetear uso mensual"
                                  >
                                    <RotateCcw className="w-3 h-3 text-amber-600" />
                                  </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-purple-600 font-medium">
                                    ⚡ Extra: {user.voice_credits_purchased || 0}
                                  </span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleOpenVoiceCreditsDialog(user)}
                                    className="hover:bg-purple-100 p-1"
                                    title="Gestionar créditos extra"
                                  >
                                    <Settings className="w-3 h-3 text-purple-600" />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">N/A</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.is_active 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {user.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs text-slate-600 font-medium">
                              {user.last_connection ? (
                                <div className="flex flex-col">
                                  <span>{new Date(user.last_connection).toLocaleDateString('es-MX')}</span>
                                  <span className="text-[10px] text-slate-400">
                                    {new Date(user.last_connection).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              ) : (
                                <span className="text-slate-400 italic text-[10px]">Nunca</span>
                              )}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.email_verified 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {user.email_verified ? 'Sí' : 'No'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openEditDialog(user)}
                                className="hover:bg-blue-100"
                                title="Editar usuario"
                              >
                                <Edit className="w-4 h-4 text-blue-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => openPasswordDialog(user)}
                                className="hover:bg-purple-100"
                                title="Cambiar contraseña"
                              >
                                <Key className="w-4 h-4 text-purple-600" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleTogglePremium(
                                  user.id, 
                                  user.username, 
                                  (user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && 
                                  (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                )}
                                className={
                                  user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                    ? 'hover:bg-purple-100'
                                    : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                    ? 'hover:bg-amber-100' 
                                    : 'hover:bg-yellow-100'
                                }
                                title={
                                  user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                    ? 'Quitar Pro'
                                    : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                                    ? 'Quitar Premium'
                                    : 'Otorgar Suscripción'
                                }
                              >
                                {user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing') ? (
                                  <Gem className="w-4 h-4 text-purple-600" />
                                ) : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing') ? (
                                  <Crown className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <Crown className="w-4 h-4 text-slate-400" />
                                )}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleToggleStatus(user.id)}
                                className={user.is_active ? 'hover:bg-red-100' : 'hover:bg-green-100'}
                                title={user.is_active ? 'Desactivar' : 'Activar'}
                              >
                                <Power className={`w-4 h-4 ${user.is_active ? 'text-red-600' : 'text-green-600'}`} />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteUser(user.id)}
                                className="hover:bg-red-100"
                                title="Eliminar usuario"
                              >
                                <Trash2 className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Vista de tarjetas para móvil */}
                <div className="lg:hidden space-y-4">
                  {filteredUsers.map((user) => (
                    <Card key={user.id} className="p-4 bg-white border border-slate-200">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{user.username}</h3>
                            <p className="text-sm text-slate-600 truncate">{user.email}</p>
                            <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-1">
                              <Clock className="w-3 h-3" />
                              <span>
                                {user.last_connection 
                                  ? `Última conexión: ${new Date(user.last_connection).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                                  : 'Nunca se ha conectado'}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-1 ml-2 flex-shrink-0">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              user.role === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.role === 'admin' ? 'Admin' : 'User'}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                              ? 'bg-purple-100 text-purple-700'
                              : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                              ? 'bg-amber-100 text-amber-700' 
                              : 'bg-slate-100 text-slate-700'
                          }`}>
                            {user.subscription_tier === 'pro' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                              ? '💎 Pro'
                              : user.subscription_tier === 'premium' && (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                              ? '👑 Premium'
                              : '🆓 Free'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.is_active 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {user.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.email_verified 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {user.email_verified ? '✓ Verificado' : '⚠ No verificado'}
                          </span>
                        </div>

                        {(user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && 
                         (user.subscription_status === 'active' || user.subscription_status === 'trialing') && (
                          <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 space-y-2">
                            <p className="text-xs font-bold text-purple-900">⚡ Voice AI</p>
                            <div className="space-y-1 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="text-purple-700">Uso mensual:</span>
                                <span className="font-bold text-purple-900">{user.voice_monthly_usage || 0}/100</span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-purple-700">Créditos extra:</span>
                                <span className="font-bold text-purple-900">{user.voice_credits_purchased || 0}</span>
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2 pt-2">
                              <button
                                type="button"
                                onClick={() => handleOpenVoiceCreditsDialog(user)}
                                className="w-full py-1.5 px-2 rounded-xl border border-purple-300 bg-white hover:bg-purple-50 text-purple-700 font-extrabold text-xs flex items-center justify-center gap-1 shadow-xs transition-all"
                              >
                                <Settings className="w-3.5 h-3.5 text-purple-600 shrink-0" />
                                <span>Créditos</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => handleResetMonthlyUsage(user.id, user.username)}
                                className="w-full py-1.5 px-2 rounded-xl border border-amber-300 bg-white hover:bg-amber-50 text-amber-800 font-extrabold text-xs flex items-center justify-center gap-1 shadow-xs transition-all truncate"
                              >
                                <RotateCcw className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                <span>Resetear</span>
                              </button>
                            </div>
                          </div>
                        )}

                        <div className="pt-2 border-t border-slate-200 space-y-2">
                          <Button
                            size="sm"
                            onClick={() => handleTogglePremium(
                              user.id, 
                              user.username, 
                              (user.subscription_tier === 'premium' || user.subscription_tier === 'pro') && 
                              (user.subscription_status === 'active' || user.subscription_status === 'trialing')
                            )}
                            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-xs h-9 font-bold rounded-xl shadow-xs"
                          >
                            <Crown className="w-3.5 h-3.5 mr-1.5 text-amber-100" />
                            <span>Otorgar Suscripción / Premium</span>
                          </Button>
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(user)}
                              className="hover:bg-blue-50 text-xs h-9 font-semibold rounded-xl"
                            >
                              <Edit className="w-3.5 h-3.5 mr-1 text-blue-600" />
                              <span>Editar</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPasswordDialog(user)}
                              className="hover:bg-purple-50 text-xs h-9 font-semibold rounded-xl"
                            >
                              <Key className="w-3.5 h-3.5 mr-1 text-purple-600" />
                              <span>Clave</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleStatus(user.id)}
                              className={`text-xs h-9 font-semibold rounded-xl ${user.is_active ? 'hover:bg-red-50 text-red-600' : 'hover:bg-green-50 text-green-600'}`}
                            >
                              <Power className="w-3.5 h-3.5 mr-1" />
                              <span>{user.is_active ? 'Off' : 'On'}</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteUser(user.id)}
                              className="hover:bg-red-50 text-red-600 border-red-200 text-xs h-9 font-semibold rounded-xl"
                            >
                              <Trash2 className="w-3.5 h-3.5 mr-1" />
                              <span>Eliminar</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </Card>
            )}

            {/* TAB: SUSCRIPCIONES */}
            {activeAdminTab === 'suscripciones' && (
              <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm animate-in fade-in duration-200 space-y-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-100 pb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                      <Crown className="w-5 h-5 text-amber-600" />
                      Estadísticas y Gestión de Suscripciones
                    </h2>
                    <p className="text-xs text-slate-500">Supervisa usuarios activos, períodos de prueba y recupera vencidos</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    <Button
                      onClick={handleSyncStripePrices}
                      variant="outline"
                      className="border-purple-300 text-purple-700 hover:bg-purple-50 font-bold px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 shrink-0"
                    >
                      <Zap className="w-4 h-4 text-purple-600" />
                      Sincronizar Precios Stripe
                    </Button>
                    <Button
                      onClick={handleRecoverSubscriptions}
                      className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-4 py-2 rounded-xl shadow-sm flex items-center gap-2 text-xs shrink-0"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Recuperar Vencidas
                    </Button>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Gem className="w-4 h-4 text-purple-600" />
                      <p className="text-xs text-purple-700 font-bold uppercase">Usuarios Pro</p>
                    </div>
                    <p className="text-2xl font-black text-purple-900">
                      {users.filter(u => u.subscription_tier === 'pro' && (u.subscription_status === 'active' || u.subscription_status === 'trialing')).length}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2 mb-1">
                      <Crown className="w-4 h-4 text-amber-600" />
                      <p className="text-xs text-amber-700 font-bold uppercase">Usuarios Premium</p>
                    </div>
                    <p className="text-2xl font-black text-amber-900">
                      {users.filter(u => u.subscription_tier === 'premium' && (u.subscription_status === 'active' || u.subscription_status === 'trialing')).length}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl border border-slate-200">
                    <p className="text-xs text-slate-700 font-bold uppercase">Usuarios Free</p>
                    <p className="text-2xl font-black text-slate-900 mt-1">
                      {users.filter(u => u.subscription_tier === 'free' || !(u.subscription_status === 'active' || u.subscription_status === 'trialing')).length}
                    </p>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl border border-green-200">
                    <p className="text-xs text-green-700 font-bold uppercase">Conversión</p>
                    <p className="text-2xl font-black text-green-900 mt-1">
                      {users.length > 0 ? ((users.filter(u => (u.subscription_tier === 'premium' || u.subscription_tier === 'pro') && (u.subscription_status === 'active' || u.subscription_status === 'trialing')).length / users.length) * 100).toFixed(1) : 0}%
                    </p>
                  </div>
                </div>

                {/* Users Lists */}
                <div className="space-y-6 pt-2">
                  {/* Usuarios en Trial */}
                  {users.filter(u => u.subscription_status === 'trialing').length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-black rounded-full">TRIAL</span>
                        En Período de Prueba ({users.filter(u => u.subscription_status === 'trialing').length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {users.filter(u => u.subscription_status === 'trialing').map(user => (
                          <div 
                            key={user.id} 
                            className={`p-3 rounded-xl border-2 ${
                              user.subscription_tier === 'pro' 
                                ? 'bg-gradient-to-r from-purple-50 to-purple-100 border-purple-300'
                                : 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              {user.subscription_tier === 'pro' ? (
                                <Gem className="w-5 h-5 text-purple-600" />
                              ) : (
                                <Crown className="w-5 h-5 text-amber-600" />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-900 truncate text-sm">{user.username}</p>
                                <p className="text-xs text-slate-600 truncate">{user.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Usuarios Pro Activos */}
                  {users.filter(u => u.subscription_tier === 'pro' && u.subscription_status === 'active').length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <Gem className="w-4 h-4 text-purple-600" />
                        Usuarios Pro Activos ({users.filter(u => u.subscription_tier === 'pro' && u.subscription_status === 'active').length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {users.filter(u => u.subscription_tier === 'pro' && u.subscription_status === 'active').map(user => (
                          <div key={user.id} className="p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                            <div className="flex items-center gap-2">
                              <Gem className="w-5 h-5 text-purple-600" />
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-900 truncate text-sm">{user.username}</p>
                                <p className="text-xs text-slate-600 truncate">{user.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Usuarios Premium Activos */}
                  {users.filter(u => u.subscription_tier === 'premium' && u.subscription_status === 'active').length > 0 && (
                    <div>
                      <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <Crown className="w-4 h-4 text-amber-600" />
                        Usuarios Premium Activos ({users.filter(u => u.subscription_tier === 'premium' && u.subscription_status === 'active').length})
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {users.filter(u => u.subscription_tier === 'premium' && u.subscription_status === 'active').map(user => (
                          <div key={user.id} className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-200">
                            <div className="flex items-center gap-2">
                              <Crown className="w-5 h-5 text-amber-600" />
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-slate-900 truncate text-sm">{user.username}</p>
                                <p className="text-xs text-slate-600 truncate">{user.email}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* TAB: VOICE AI */}
            {activeAdminTab === 'voice' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    Estadísticas Voice AI
                  </h2>
                  <VoiceAIStats token={token} />
                </Card>
                <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-600" />
                    Paquetes Voice AI
                  </h2>
                  <VoiceAIPackageManagement token={token} />
                </Card>
              </div>
            )}

            {/* TAB: PUSH PRO / MENSAJES */}
            {activeAdminTab === 'mensajes' && (
              <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm animate-in fade-in duration-200">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                  Centro de Mensajes (Push Pro)
                </h2>
                <AdminNotifications token={token} />
              </Card>
            )}

            {/* TAB: REPORTES / FEEDBACK */}
            {activeAdminTab === 'reportes' && (
              <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm animate-in fade-in duration-200">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-600" />
                  Reportes y Feedback de Usuarios
                </h2>
                <FeedbackManagement token={token} />
              </Card>
            )}

            {/* TAB: SISTEMA */}
            {activeAdminTab === 'sistema' && (
              <div className="space-y-6 animate-in fade-in duration-200">
                <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-purple-600" />
                    Configuración de Inactividad
                  </h2>
                  <InactivitySettings token={token} />
                </Card>
                <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-emerald-600" />
                    Códigos Promocionales
                  </h2>
                  <PromoCodeManagement token={token} />
                </Card>
                <Card className="p-4 sm:p-6 bg-white border-slate-200 shadow-sm">
                  <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-amber-600" />
                    Mantenimiento del Sistema
                  </h2>
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Tag className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">Reparar Categorías por Defecto</h3>
                        <p className="text-xs text-slate-600 mb-4">
                          Crea las categorías por defecto para usuarios registrados sin ellas (ej. Google Auth).
                        </p>
                        <Button
                          onClick={async () => {
                            if (!window.confirm('¿Ejecutar reparación de categorías?')) return;
                            try {
                              const response = await axios.post(`${API}/admin/repair-categories`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              showToast.success(`✅ ${response.data.message}`);
                            } catch (error) {
                              showToast.error('Error al reparar categorías: ' + (error.response?.data?.detail || error.message));
                            }
                          }}
                          className="bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs"
                        >
                          <Tag className="w-4 h-4 mr-2" />
                          Ejecutar Reparación
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl mt-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <RotateCcw className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900 mb-1">Recuperar Suscripciones Vencidas</h3>
                        <p className="text-xs text-slate-600 mb-4">
                          Verifica en Stripe usuarios con suscripción activa o renovada para sincronizar su estado en la plataforma.
                        </p>
                        <Button
                          onClick={handleRecoverSubscriptions}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-2"
                        >
                          <RotateCcw className="w-4 h-4" />
                          Recuperar Suscripciones
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            )}
            </div>
          </div>
        </>
      )}
      {/* Edit User Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Editar Usuario</DialogTitle>
              <DialogDescription>Modifica los datos del perfil y rol del usuario.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Nombre de Usuario</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Rol</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">Usuario</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Actualizar Usuario
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Cambiar Contraseña de {changingPassword?.username}</DialogTitle>
              <DialogDescription>Establece una nueva contraseña de acceso para este usuario.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new_password">Nueva Contraseña</Label>
                <div className="relative">
                  <Input
                    id="new_password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ new_password: e.target.value })}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Mínimo 6 caracteres</p>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Cambiar Contraseña
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Modal de Opciones de Suscripción y Stripe */}
        <Dialog open={isSubscriptionOptionsModalOpen} onOpenChange={setIsSubscriptionOptionsModalOpen}>
          <DialogContent className="w-[94vw] max-w-md mx-auto p-5 sm:p-6 rounded-3xl overflow-hidden">
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-base sm:text-lg font-extrabold text-slate-900 leading-snug break-words flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-500 shrink-0" />
                <span>Suscripción: <span className="text-blue-600 font-black">{selectedUserForOptions?.username}</span></span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Plan actual: <span className="font-bold uppercase text-slate-700">{selectedUserForOptions?.subscription_tier || 'free'}</span> ({selectedUserForOptions?.subscription_status || 'inactivo'})
                {selectedUserForOptions?.stripe_customer_id && (
                  <span className="block text-[11px] font-mono text-slate-400 truncate mt-0.5">
                    Stripe Customer: {selectedUserForOptions.stripe_customer_id}
                  </span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 pt-2">
              {/* Acciones de Stripe */}
              <div className="space-y-2">
                <p className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">Acciones Stripe</p>
                
                {/* Botón Sincronizar con Stripe */}
                <button
                  type="button"
                  disabled={stripeActionLoading}
                  onClick={() => handleSyncUserStripe(selectedUserForOptions?.id, selectedUserForOptions?.username)}
                  className="w-full text-left p-3 rounded-2xl bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-emerald-600 text-white group-hover:scale-105 transition-transform shrink-0">
                      <CheckCircle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-emerald-900">Sincronizar suscripción activa de Stripe</p>
                      <p className="text-[10px] text-emerald-700 leading-tight">Detecta su plan Pro activo en Stripe (por su correo) y lo vincula de inmediato a PGastos.</p>
                    </div>
                  </div>
                </button>

                {/* Botón Reembolsar último cobro y Cancelar */}
                <button
                  type="button"
                  disabled={stripeActionLoading}
                  onClick={() => handleRefundAndCancel(selectedUserForOptions?.id, selectedUserForOptions?.username)}
                  className="w-full text-left p-3 rounded-2xl bg-red-50 hover:bg-red-100 border border-red-200 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-red-600 text-white group-hover:scale-105 transition-transform shrink-0">
                      <RotateCcw className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-red-900">Reembolsar último cobro y Cancelar</p>
                      <p className="text-[10px] text-red-700 leading-tight">Devuelve el dinero a su tarjeta bancaria y cancela la suscripción de inmediato en Stripe.</p>
                    </div>
                  </div>
                </button>

                {/* Botón Cancelar Suscripción */}
                <button
                  type="button"
                  disabled={stripeActionLoading}
                  onClick={() => handleCancelStripeSubscription(selectedUserForOptions?.id, selectedUserForOptions?.username)}
                  className="w-full text-left p-3 rounded-2xl bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-amber-600 text-white group-hover:scale-105 transition-transform shrink-0">
                      <AlertCircle className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-amber-900">Cancelar Suscripción en Stripe</p>
                      <p className="text-[10px] text-amber-700 leading-tight">Cancela de inmediato en Stripe sin reembolso. El usuario pasa a plan Free.</p>
                    </div>
                  </div>
                </button>

                {/* Botón Acoplar al Precio Actual ($129 MXN / mes) */}
                <button
                  type="button"
                  disabled={stripeActionLoading}
                  onClick={() => handleUpdateToCurrentPrice(selectedUserForOptions?.id, selectedUserForOptions?.username)}
                  className="w-full text-left p-3 rounded-2xl bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-all group disabled:opacity-50"
                >
                  <div className="flex items-center gap-2.5">
                    <div className="p-2 rounded-xl bg-blue-600 text-white group-hover:scale-105 transition-transform shrink-0">
                      <Zap className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-blue-900">Acoplar al precio actual ($129 MXN / mes)</p>
                      <p className="text-[10px] text-blue-700 leading-tight">Para usuarios con precio viejo ($199). Actualiza en Stripe para que su próxima renovación sea de $129 MXN.</p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Acciones Locales / Manuales */}
              <div className="space-y-2 pt-1 border-t border-slate-100">
                <p className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">Gestión Manual (PGastos)</p>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    onClick={() => {
                      setIsSubscriptionOptionsModalOpen(false);
                      setSelectedUserForSubscription(selectedUserForOptions);
                      setSubscriptionFormData({
                        tier: selectedUserForOptions?.subscription_tier === 'pro' ? 'pro' : 'premium',
                        duration: 'monthly',
                        trial_days: 0
                      });
                      setIsSubscriptionDialogOpen(true);
                    }}
                    variant="outline"
                    className="text-xs font-bold rounded-xl h-10 border-slate-200 hover:bg-slate-50 text-slate-700"
                  >
                    <Crown className="w-3.5 h-3.5 mr-1 text-amber-600" />
                    Asignar Plan
                  </Button>
                  <Button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm(`¿Revocar acceso local a ${selectedUserForOptions?.username}?`)) return;
                      try {
                        await axios.post(`${API}/admin/revoke-subscription/${selectedUserForOptions.id}`, {}, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        showToast.success('Acceso revocado localmente');
                        setIsSubscriptionOptionsModalOpen(false);
                        fetchData();
                      } catch (err) {
                        showToast.error('Error al revocar acceso');
                      }
                    }}
                    variant="outline"
                    className="text-xs font-bold rounded-xl h-10 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <AlertCircle className="w-3.5 h-3.5 mr-1 text-red-500" />
                    Revocar Local
                  </Button>
                </div>
              </div>

              <div className="pt-2">
                <Button
                  type="button"
                  onClick={() => setIsSubscriptionOptionsModalOpen(false)}
                  variant="ghost"
                  className="w-full text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-xl h-9"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Grant Subscription Dialog */}
        <Dialog open={isSubscriptionDialogOpen} onOpenChange={setIsSubscriptionDialogOpen}>
          <DialogContent className="w-[94vw] max-w-md mx-auto p-4 sm:p-6 rounded-3xl overflow-hidden">
            <DialogHeader className="pr-6 text-left">
              <DialogTitle className="text-base sm:text-lg font-extrabold text-slate-900 leading-snug break-words">
                Otorgar Suscripción a <span className="text-blue-600 font-black">{selectedUserForSubscription?.username}</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Asigna manualmente un plan a este usuario.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 pt-1 pb-2">
              <div className="space-y-1">
                <Label htmlFor="tier" className="text-xs font-bold text-slate-700">Tipo de Suscripción</Label>
                <Select 
                  value={subscriptionFormData.tier} 
                  onValueChange={(value) => setSubscriptionFormData({...subscriptionFormData, tier: value})}
                >
                  <SelectTrigger className="rounded-xl h-10 text-xs font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-w-[90vw]">
                    <SelectItem value="premium">
                      <div className="flex items-center gap-2 text-xs">
                        <Crown className="w-4 h-4 text-blue-600 shrink-0" />
                        <span className="truncate">Premium (100 voz/mes)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="pro">
                      <div className="flex items-center gap-2 text-xs">
                        <Gem className="w-4 h-4 text-purple-600 shrink-0" />
                        <span className="truncate">Pro (300 voz/mes)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="duration" className="text-xs font-bold text-slate-700">Duración</Label>
                <Select 
                  value={subscriptionFormData.duration} 
                  onValueChange={(value) => setSubscriptionFormData({...subscriptionFormData, duration: value})}
                >
                  <SelectTrigger className="rounded-xl h-10 text-xs font-semibold">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">
                      <div className="flex items-center gap-2 text-xs">
                        <CreditCard className="w-4 h-4 text-green-600" />
                        <span>Mensual</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="annual">
                      <div className="flex items-center gap-2 text-xs">
                        <CreditCard className="w-4 h-4 text-blue-600" />
                        <span>Anual (12 meses)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Info about trial upgrades */}
              {selectedUserForSubscription && 
               selectedUserForSubscription.subscription_tier === 'premium' && 
               selectedUserForSubscription.subscription_status === 'active' && 
               subscriptionFormData.tier === 'pro' &&
               subscriptionFormData.trial_days > 0 && (
                <div className="p-2.5 bg-blue-50 border border-blue-200 rounded-xl">
                  <p className="text-xs text-blue-900 leading-tight">
                    <span className="font-semibold">ℹ️ Trial Upgrade:</span> El usuario volverá a Premium automáticamente al terminar el trial de Pro.
                  </p>
                </div>
              )}

              <div className="space-y-1">
                <Label htmlFor="trial_days" className="text-xs font-bold text-slate-700">Días de Prueba (Opcional)</Label>
                <Input
                  id="trial_days"
                  type="number"
                  min="0"
                  max="365"
                  value={subscriptionFormData.trial_days}
                  onChange={(e) => setSubscriptionFormData({...subscriptionFormData, trial_days: e.target.value})}
                  placeholder="Ej: 7 días, 30 días, etc."
                  className="rounded-xl h-10 text-xs"
                />
                <p className="text-[11px] text-slate-500 leading-tight">
                  {subscriptionFormData.trial_days > 0 
                    ? `Durante ${subscriptionFormData.trial_days} días el usuario tendrá acceso completo.` 
                    : '0 días = Se agrega 1 día de gracia automáticamente.'}
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleGrantSubscription}
                  className="flex-1 rounded-xl h-11 text-xs font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-md"
                >
                  Otorgar Suscripción
                </Button>
                <Button
                  onClick={() => setIsSubscriptionDialogOpen(false)}
                  variant="outline"
                  className="flex-1 rounded-xl h-11 text-xs font-bold border-slate-200"
                >
                  Cancelar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Voice AI Credits Management Dialog */}
        <Dialog open={isVoiceCreditsDialogOpen} onOpenChange={setIsVoiceCreditsDialogOpen}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-purple-600" />
                Gestionar Voice AI
              </DialogTitle>
              <DialogDescription>Administra los créditos mensuales y extra de inteligencia artificial.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-purple-900 mb-2">
                  {selectedUserForVoiceCredits?.username}
                </p>
                <div className="space-y-1">
                  <p className="text-xs text-purple-700">
                    📊 Uso mensual: <span className="font-bold">{selectedUserForVoiceCredits?.voice_monthly_usage || 0} / 100</span>
                  </p>
                  <p className="text-xs text-purple-700">
                    ⚡ Créditos extra: <span className="font-bold">{selectedUserForVoiceCredits?.voice_credits_purchased || 0}</span>
                  </p>
                </div>
              </div>

              {/* Modificar Uso Mensual */}
              <div className="space-y-2 pb-2 border-b border-slate-200">
                <Label htmlFor="monthly_usage_amount" className="flex items-center gap-2 text-sm">
                  <span>📊 Ajustar Créditos Disponibles</span>
                </Label>
                <input
                  id="monthly_usage_amount"
                  type="text"
                  value={voiceMonthlyUsageAmount ? formatCurrencyInput(voiceMonthlyUsageAmount) : ''}
                  onChange={(e) => {
                    const rawValue = parseCurrency(e.target.value);
                    setVoiceMonthlyUsageAmount(rawValue);
                  }}
                  placeholder="Ej: 10.50 agregar, -5.25 quitar"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500">
                  💡 Positivo: Agregar créditos disponibles | Negativo: Quitar créditos disponibles
                </p>
                <Button
                  onClick={handleUpdateMonthlyUsage}
                  disabled={!voiceMonthlyUsageAmount || voiceMonthlyUsageAmount === '0'}
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-sm"
                  size="sm"
                >
                  Ajustar Créditos
                </Button>
              </div>

              {/* Modificar Créditos Extra */}
              <div className="space-y-2 pb-2">
                <Label htmlFor="credits_amount" className="flex items-center gap-2 text-sm">
                  <span>⚡ Modificar Créditos Extra</span>
                </Label>
                <input
                  id="credits_amount"
                  type="text"
                  value={voiceCreditsAmount ? formatCurrencyInput(voiceCreditsAmount) : ''}
                  onChange={(e) => {
                    const rawValue = parseCurrency(e.target.value);
                    setVoiceCreditsAmount(rawValue);
                  }}
                  placeholder="Ej: 50.00 o -25.50"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-slate-500">
                  💡 Positivo agregar, negativo quitar
                </p>
                <Button
                  onClick={handleUpdateVoiceCredits}
                  disabled={!voiceCreditsAmount || voiceCreditsAmount === '0'}
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-sm"
                  size="sm"
                >
                  Actualizar Créditos
                </Button>
              </div>

              {/* Resetear Uso Mensual */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                <p className="text-xs text-amber-800 mb-2">
                  <strong>🔄 Resetear:</strong> Elimina todo el uso mensual.
                </p>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsVoiceCreditsDialogOpen(false);
                    handleResetMonthlyUsage(selectedUserForVoiceCredits?.id, selectedUserForVoiceCredits?.username);
                  }}
                  variant="outline"
                  className="w-full border-amber-300 text-amber-700 hover:bg-amber-100 text-xs"
                >
                  🔄 Resetear a 0
                </Button>
              </div>

              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => {
                    setIsVoiceCreditsDialogOpen(false);
                    setVoiceCreditsAmount('');
                    setVoiceMonthlyUsageAmount('');
                  }}
                  variant="outline"
                  className="flex-1 text-sm"
                  size="sm"
                >
                  Cerrar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* User Stats View */}
        {view === 'user-stats' && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setView('main')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <h2 className="text-2xl font-bold text-slate-900">Estadísticas por Usuario</h2>
            </div>

            <Card className="p-6 bg-white/80 backdrop-blur-sm border-slate-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Usuario</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Email</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Ingresos</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Gastos</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Ahorros</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-slate-700">Transacciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userStats.map((user) => (
                      <tr key={user.user_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-4 text-sm font-medium text-slate-900">{user.username}</td>
                        <td className="py-4 px-4 text-sm text-slate-600">{user.email}</td>
                        <td className="py-4 px-4 text-sm text-right text-green-600 font-semibold">
                          {formatCurrency(user.total_income)}
                        </td>
                        <td className="py-4 px-4 text-sm text-right text-red-600 font-semibold">
                          {formatCurrency(user.total_expenses)}
                        </td>
                        <td className="py-4 px-4 text-sm text-right text-blue-600 font-semibold">
                          {formatCurrency(user.total_savings)}
                        </td>
                        <td className="py-4 px-4 text-sm text-right text-slate-700 font-medium">
                          {user.transaction_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AdminPanel;